/* eslint-disable react-hooks/exhaustive-deps */
/**
 * EditableReview — Body vs Garment split.
 *
 * Two clearly-labelled sections:
 *   1. "Your Body Measurements (3DLOOK)" — read-only pass-through of the
 *      scanned body girths. Never mutated by fit choices.
 *   2. "Garment Cutting Measurements" — computed by the Fit Fusion Engine
 *      as body + category-specific ease per fit_pref. Re-computed on the
 *      backend every time the customer changes Tighten / Regular / Loosen.
 *
 * Checkout receives the GARMENT measurements. The BODY is attached
 * separately for tailor audit + future recalculation.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { measurementsAPI } from '../api';
import {
  ShieldCheck, Plus, Minus, Sparkles, Check, ArrowRight, ChevronLeft,
  ChevronDown, ChevronUp, HelpCircle, Ruler,
} from 'lucide-react';
import { humanize } from '../data/terminology';
import {
  CUSTOMER_HOW_TO, fieldConfidence, ESSENTIAL_FIELDS,
} from '../data/customerMeasurementHelp';
import LuxuryAvatar from './LuxuryAvatar';
import ProviderMeasurements from './ProviderMeasurements';
import { PROVENANCE_LABEL, withEdits } from '../data/measurementProvenance';

const GOLD = '#D4AF37';
const INK = '#0F0F0F';
const MUTED = '#595959';
const LINE = '#E5E5E5';
const SOFT = '#FAF9F6';
const GREEN = '#166534';

const useAnimNumber = (target, dur = 900) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (target == null) return;
    let raf, start; const from = v;
    const tick = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / dur);
      setV(Math.round(from + (target - from) * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [target]);
  return v;
};

// Returns visible (non-hidden) field keys, in display order
const visibleFields = (obj) => Object.keys(obj || {}).filter(k => humanize(k) !== null);

// Sanitize: digits only, max 3 chars (covers 0-999 cm)
const digitsOnly = (s, max = 3) => String(s).replace(/[^0-9]/g, '').slice(0, max);

// ─── Read-only Body row (3DLOOK measurement) ─────────────────────────────
const BodyRow = ({ fieldKey, value }) => (
  <div className="border-b" style={{ borderColor: '#F5F5F5' }}>
    <div className="px-3 py-2 flex items-center justify-between gap-2">
      <p className="text-[12px] truncate" style={{ color: INK }}>{humanize(fieldKey)}</p>
      <span data-testid={`body-${fieldKey}`}
        className="text-[13px] tabular-nums"
        style={{ fontFamily: 'Playfair Display, serif', color: INK, fontWeight: 500 }}>
        {typeof value === 'number' ? value.toFixed(1) : value} <span className="text-[9px]" style={{ color: MUTED }}>cm</span>
      </span>
    </div>
  </div>
);

// ─── Editable Garment row ─────────────────────────────────────────────────
const GarmentRow = ({ fieldKey, derivedValue, currentEdit, onCommit, confidence, easeCm, provenance }) => {
  const canonical = currentEdit ?? derivedValue;
  const [str, setStr] = useState(String(canonical));
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => { setStr(String(canonical)); }, [canonical]);

  const commitBlur = () => {
    const n = parseInt(str, 10);
    if (Number.isNaN(n) || n < 0) { setStr(String(canonical)); return; }
    if (n !== canonical) onCommit(n);
  };

  const stepBy = (delta) => {
    const parsed = parseInt(str, 10);
    const base = Number.isNaN(parsed) ? canonical : parsed;
    const next = Math.max(0, base + delta);
    setStr(String(next));
    if (next !== canonical) onCommit(next);
  };

  const helpText = CUSTOMER_HOW_TO[fieldKey];
  const confColor = confidence == null ? null
    : confidence >= 80 ? GREEN
    : confidence >= 65 ? GOLD
    : '#9A6A0F';

  return (
    <div className="border-b" style={{ borderColor: '#F5F5F5' }}>
      <div className="px-3 py-2.5 flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[12px] truncate" style={{ color: INK }}>{humanize(fieldKey)}</p>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            {confidence != null && (
              <span data-testid={`conf-${fieldKey}`}
                className="text-[9px] tracking-[0.06em] px-1.5 py-0.5"
                style={{
                  backgroundColor: confidence >= 80 ? 'rgba(22,101,52,0.10)'
                    : confidence >= 65 ? 'rgba(212,175,55,0.14)'
                    : 'rgba(154,106,15,0.12)',
                  color: confColor, fontWeight: 600, letterSpacing: '0.04em',
                }}>
                {confidence}% AI
              </span>
            )}
            {provenance && (
              <span data-testid={`prov-${fieldKey}`} className="text-[9px] tracking-[0.04em] px-1.5 py-0.5"
                style={{ backgroundColor: '#F5F3EE', color: MUTED, fontWeight: 600 }}>
                {PROVENANCE_LABEL[provenance] || provenance}
              </span>
            )}
            {typeof easeCm === 'number' && easeCm !== 0 && (
              <span data-testid={`ease-${fieldKey}`}
                className="text-[9px] tracking-[0.04em] px-1.5 py-0.5"
                style={{ backgroundColor: 'rgba(212,175,55,0.10)', color: GOLD, fontWeight: 600 }}>
                {easeCm > 0 ? '+' : ''}{easeCm} cm ease
              </span>
            )}
            {helpText && (
              <button
                data-testid={`help-${fieldKey}`}
                onClick={() => setShowHelp(s => !s)}
                className="text-[9px] tracking-[0.06em] flex items-center gap-0.5"
                style={{ color: MUTED }}>
                <HelpCircle className="w-2.5 h-2.5" strokeWidth={1.5} />
                How to measure
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            data-testid={`dec-${fieldKey}`}
            onClick={() => stepBy(-1)}
            className="w-7 h-7 flex items-center justify-center"
            style={{ border: `1px solid ${LINE}` }}>
            <Minus className="w-2.5 h-2.5" style={{ color: INK }} strokeWidth={1.5} />
          </button>
          <input
            data-testid={`val-${fieldKey}`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            value={str}
            onChange={(e) => setStr(digitsOnly(e.target.value, 3))}
            onBlur={commitBlur}
            className="w-14 text-center py-1 text-[13px] focus:outline-none"
            style={{ fontFamily: 'Playfair Display, serif', color: INK, border: `1px solid ${LINE}` }} />
          <button
            data-testid={`inc-${fieldKey}`}
            onClick={() => stepBy(1)}
            className="w-7 h-7 flex items-center justify-center"
            style={{ border: `1px solid ${LINE}` }}>
            <Plus className="w-2.5 h-2.5" style={{ color: INK }} strokeWidth={1.5} />
          </button>
          <span className="text-[9px] w-6" style={{ color: MUTED }}>cm</span>
        </div>
      </div>
      {showHelp && helpText && (
        <div data-testid={`help-text-${fieldKey}`}
          className="px-3 pb-2.5 -mt-1 text-[10.5px]"
          style={{ color: MUTED, lineHeight: 1.5 }}>
          <span style={{ color: GOLD, fontWeight: 600 }}>Tip · </span>{helpText}
        </div>
      )}
    </div>
  );
};

const buildReasons = (result, photoCount, payload) => {
  const reasons = [];
  const hasScan = result?.body && Object.keys(result.body).length > 0;
  if (hasScan) reasons.push({ delta: 20, label: 'AI Body Scan applied' });
  if (photoCount > 0) reasons.push({ delta: 10, label: 'Photo verified your shoulders' });
  if (photoCount >= 2) reasons.push({ delta: 3, label: 'Side angle improved accuracy' });
  if (photoCount >= 3) reasons.push({ delta: 2, label: 'Back angle confirmed proportions' });
  if (payload?.usual_size && !hasScan) reasons.push({ delta: 5, label: `Size ${payload.usual_size} matched tailor data` });
  if (result?.bmi != null && result.bmi >= 17 && result.bmi <= 30)
    reasons.push({ delta: 8, label: `BMI plausible (${result.bmi})` });
  reasons.push({ delta: 3, label: 'Cross-checks passed' });
  return reasons;
};

const EditableReview = ({ result, payload, schemaKey, schemaLabel, photoCount = 0,
                         onContinue, onAdjustFit, onBack, source = 'fusion' }) => {
  const [edits, setEdits] = useState({});
  const [recomputing, setRecomputing] = useState(false);
  const [liveResult, setLiveResult] = useState(result);

  // BODY is immutable — always taken from the initial result (the 3DLOOK scan).
  // GARMENT re-computes when the customer changes fit preference.
  const body = liveResult?.body || {};
  const derived = liveResult?.derived || {};
  const easeApplied = liveResult?.ease_applied || {};
  const provenance = withEdits(liveResult?.derived_provenance || {}, edits);
  const hasScan = body && Object.keys(body).length > 0;
  // Provider list comes from the initial result (immutable); recompute responses never carry it.
  const providerData = result?.provider_measurements;

  const bodyFields = useMemo(() => visibleFields(body), [body]);
  const garmentFields = useMemo(() => visibleFields(derived), [derived]);
  const { essentialKeys, advancedKeys } = useMemo(() => {
    const ess = []; const adv = [];
    for (const k of garmentFields) {
      if (typeof derived[k] !== 'number') continue;
      if (ESSENTIAL_FIELDS.has(k)) ess.push(k); else adv.push(k);
    }
    return { essentialKeys: ess, advancedKeys: adv };
  }, [garmentFields, derived]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showBodyDetail, setShowBodyDetail] = useState(false);

  const conf = liveResult?.fit_confidence || 0;
  const animConf = useAnimNumber(conf, 1100);
  const reasons = buildReasons(liveResult, photoCount, payload);

  const confColor = animConf >= 80 ? GREEN : animConf >= 65 ? GOLD : '#991B1B';

  const setEditValue = (k, n) => setEdits(p => ({ ...p, [k]: n }));

  // ── Re-run the Fit Fusion Engine with the customer's BODY as authoritative
  // base — the engine adds category-specific ease for the chosen fit.
  const recompute = async (newFit) => {
    setRecomputing(true);
    try {
      const r = await measurementsAPI.fuse({
        category: schemaKey,
        // Scan context (height/weight/gender) always comes from the initial scan result —
        // engine responses don't carry it, so liveResult would lose it after one recompute.
        gender: result?.gender || payload.gender,
        height_cm: result?.height_cm ?? payload.height_cm,
        weight_kg: result?.weight_kg ?? payload.weight_kg,
        usual_size: liveResult?.suggested_size || payload.usual_size,
        fit_pref: newFit || payload.fit_pref,
        answers: payload.answers || {},
        photo_provided: photoCount > 0,
        // Feed the immutable body values back in so ease is applied to the
        // customer's ACTUAL scan and not to a hard-coded size-table row.
        body_measurements: hasScan ? body : undefined,
      });
      // Preserve the body pass-through — the API returns it too, but merge
      // defensively so an engine response without `body` cannot erase it.
      setLiveResult(prev => ({
        ...prev,
        ...r,
        body: (r.body && Object.keys(r.body).length) ? r.body : body,
      }));
      setEdits({});
    } catch { /* keep previous result */ }
    setRecomputing(false);
  };

  const continueWithEdits = () => {
    // Garment (cutting) measurements are what the TAILOR needs.
    const finalGarment = { ...derived, ...edits, fit: payload.fit_pref };
    onContinue({
      measurements: finalGarment,
      body_measurements: hasScan ? body : undefined,
      ease_applied: easeApplied,
      measurement_provenance: provenance,
      suggested_size: liveResult.suggested_size,
      fit_confidence: liveResult.fit_confidence,
      schema: schemaLabel,
    });
  };

  return (
    <div data-testid="editable-review" className="h-[100dvh] flex flex-col" style={{ backgroundColor: SOFT }}>
      {/* Header */}
      <div className="shrink-0 sticky top-0 z-50 bg-white border-b" style={{ borderColor: LINE }}>
        <div className="flex items-center justify-between px-4 py-3">
          <button data-testid="review-back-btn" onClick={onBack} className="p-1.5 -ml-1.5">
            <ChevronLeft className="w-5 h-5" style={{ color: INK }} strokeWidth={1.5} />
          </button>
          <div className="text-center flex-1">
            <p className="text-[9px] tracking-[0.22em] uppercase" style={{ color: GOLD }}>
              {hasScan ? 'AI Body Scan Applied' : 'AI Fit Complete'}
            </p>
            <p className="text-[15px]" style={{ fontFamily: 'Playfair Display, serif', color: INK, fontWeight: 500 }}>Your Perfect Fit</p>
            <p className="text-[9px]" style={{ color: MUTED }}>{schemaLabel}{source === 'fusion' && photoCount > 0 ? ` · ${photoCount} photos analyzed` : ''}</p>
          </div>
          <div className="w-9" />
        </div>
        <div className="h-0.5" style={{ backgroundColor: GOLD }} />
      </div>

      <div data-testid="review-scroll" className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
        {/* Saved-profile reassurance banner */}
        <div data-testid="saved-profile-banner"
          className="mb-3 px-3.5 py-2.5 flex items-center gap-2"
          style={{
            background: 'linear-gradient(135deg, rgba(22,101,52,0.05), rgba(212,175,55,0.05))',
            border: `1px solid rgba(22,101,52,0.30)`,
          }}>
          <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" style={{ color: GREEN }} strokeWidth={1.6} />
          <div className="flex-1">
            <p className="text-[11px]" style={{ color: INK, fontWeight: 500 }}>
              {hasScan ? 'Saved to your Lunora’s fit profile' : 'Saved to your AI fit profile'}
            </p>
            <p className="text-[9.5px]" style={{ color: MUTED }}>Reuse for future {schemaLabel} orders</p>
          </div>
        </div>

        {/* Avatar Hero */}
        <div data-testid="avatar-hero" className="bg-white mb-3 flex flex-col items-center pt-4 pb-4"
          style={{ border: `1px solid ${LINE}` }}>
          <p className="text-[9px] tracking-[0.22em] uppercase mb-1" style={{ color: GOLD }}>Live Preview</p>
          <p className="text-[10px] mb-3" style={{ color: MUTED }}>Updates as you adjust fit and style</p>
          <LuxuryAvatar
            gender={liveResult?.gender || payload.gender || 'female'}
            schemaKey={schemaKey}
            fitPref={payload.fit_pref || 'regular'}
            confidence={animConf}
            size={300}
          />
        </div>

        {/* Confidence Hero */}
        <div className="bg-white p-5 text-center mb-3" style={{ border: `1px solid ${LINE}` }}>
          <p className="text-[10px] tracking-[0.18em] uppercase mb-2" style={{ color: GOLD }}>Fit Confidence</p>
          <div className="relative w-32 h-32 mx-auto mb-2">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
              <circle cx="50" cy="50" r="42" fill="none" stroke={LINE} strokeWidth="6" />
              <circle cx="50" cy="50" r="42" fill="none" stroke={confColor} strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={`${(animConf / 100) * 264} 264`}
                style={{ transition: 'stroke-dasharray 0.6s ease-out' }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <p data-testid="confidence-pct" className="text-[34px] leading-none"
                style={{ fontFamily: 'Playfair Display, serif', color: confColor, fontWeight: 500 }}>
                {animConf}%
              </p>
            </div>
          </div>
          <p className="text-[16px] mb-0.5" style={{ fontFamily: 'Playfair Display, serif', color: INK, fontWeight: 500 }}>
            Suggested Size: <span data-testid="suggested-size" style={{ color: GOLD }}>{liveResult?.suggested_size}</span>
          </p>
        </div>

        {/* Reasoning chips */}
        <div className="bg-white p-3 mb-3" style={{ border: `1px solid ${LINE}` }}>
          <p className="text-[10px] tracking-[0.12em] uppercase mb-2" style={{ color: MUTED }}>Why You Can Trust This</p>
          <div className="space-y-1">
            {reasons.map((r, i) => (
              <div key={i} data-testid={`reason-${i}`}
                className="flex items-center gap-2 py-1 reason-chip"
                style={{ '--i': i, animation: `slide-in 280ms ease-out backwards`, animationDelay: `${i * 60}ms` }}>
                <span className="text-[11px] w-9" style={{ color: GREEN, fontWeight: 600 }}>+{r.delta}</span>
                <span className="text-[11px] flex-1" style={{ color: INK }}>{r.label}</span>
                <Check className="w-3 h-3" style={{ color: GREEN }} strokeWidth={2} />
              </div>
            ))}
          </div>
        </div>

        {/* Fit override — regenerates garment; body stays untouched */}
        <div className="mb-3">
          <p className="text-[10px] tracking-[0.12em] uppercase mb-1.5" style={{ color: MUTED }}>Adjust Fit</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { v: 'slim',         l: 'Slim' },
              { v: 'regular',      l: 'Regular' },
              { v: 'relaxed',      l: 'Relaxed' },
              { v: 'luxury_loose', l: 'Luxury Loose' },
            ].map(o => (
              <button key={o.v} data-testid={`fit-override-${o.v}`}
                onClick={() => { onAdjustFit(o.v); recompute(o.v); }}
                disabled={recomputing}
                className="py-2.5 text-[10.5px] tracking-[0.04em] transition disabled:opacity-50"
                style={{
                  backgroundColor: payload.fit_pref === o.v ? INK : '#FFF',
                  color: payload.fit_pref === o.v ? '#FFF' : INK,
                  border: `1px solid ${payload.fit_pref === o.v ? INK : LINE}`,
                  fontWeight: 500,
                }}>{o.l}</button>
            ))}
          </div>
        </div>

        {/* ─── SECTION 1: BODY (3DLOOK) — read-only, immutable ─── */}
        {hasScan && (
          <div className="bg-white mb-3" style={{ border: `1px solid ${LINE}` }} data-testid="body-measurements-section">
            <button
              data-testid="body-measurements-toggle"
              onClick={() => setShowBodyDetail(s => !s)}
              className="w-full px-3 py-3 flex items-center justify-between border-b"
              style={{ borderColor: LINE }}>
              <div className="text-left">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Ruler className="w-3.5 h-3.5" style={{ color: GREEN }} strokeWidth={1.6} />
                  <p className="text-[10.5px] tracking-[0.18em] uppercase" style={{ color: GREEN, fontWeight: 600 }}>
                    Your Body Measurements
                  </p>
                </div>
                <p className="text-[10.5px]" style={{ color: MUTED, lineHeight: 1.5 }}>
                  Captured by your AI Body Scan · never modified by fit choices
                </p>
              </div>
              {showBodyDetail
                ? <ChevronUp  className="w-4 h-4" style={{ color: MUTED }} strokeWidth={1.5} />
                : <ChevronDown className="w-4 h-4" style={{ color: MUTED }} strokeWidth={1.5} />}
            </button>
            {showBodyDetail && (providerData?.count > 0
              ? <ProviderMeasurements data={providerData} />
              : (
                <div data-testid="body-canonical-fallback">
                  <p className="px-3 py-2 text-[10px]" style={{ color: MUTED }}>
                    Full 3DLOOK measurement list is not stored for this profile — showing scan summary values.
                  </p>
                  {bodyFields
                    .filter(k => typeof body[k] === 'number' && !(k === 'bust' && body.bust === body.chest))
                    .map(k => <BodyRow key={k} fieldKey={k} value={body[k]} />)}
                </div>
              ))}
          </div>
        )}

        {/* ─── SECTION 2: GARMENT (Cutting) — editable, fit-driven ─── */}
        <div className="bg-white" style={{ border: `1px solid ${LINE}` }} data-testid="ai-measurements-section">
          <div className="px-3 py-3 border-b" style={{ borderColor: LINE }}>
            <div className="flex items-center gap-1.5 mb-0.5">
              <Sparkles className="w-3.5 h-3.5" style={{ color: GOLD }} strokeWidth={1.6} />
              <p className="text-[10.5px] tracking-[0.18em] uppercase" style={{ color: GOLD, fontWeight: 600 }}>
                Garment Cutting Measurements
              </p>
            </div>
            <p className="text-[10.5px]" style={{ color: MUTED, lineHeight: 1.5 }}>
              {hasScan
                ? 'Your body + ease for the selected fit. This is what the tailor cuts to.'
                : "Our AI handled the tailoring. Tweak any value if you'd like — your tailor can fine-tune later."}
            </p>
          </div>
          {essentialKeys.map(k => (
            <GarmentRow
              key={k}
              fieldKey={k}
              derivedValue={derived[k]}
              currentEdit={edits[k]}
              onCommit={(n) => setEditValue(k, n)}
              confidence={fieldConfidence({ fieldKey: k, baseConfidence: animConf || conf, hasPhoto: photoCount > 0 })}
              easeCm={easeApplied[k]}
              provenance={provenance[k]}
            />
          ))}
        </div>

        {/* Advanced Tailoring Options — optional refinement */}
        {advancedKeys.length > 0 && (
          <div className="mt-2" data-testid="advanced-options-section">
            <button
              data-testid="advanced-options-toggle"
              onClick={() => setShowAdvanced(s => !s)}
              className="w-full px-3 py-2.5 flex items-center justify-between"
              style={{
                background: showAdvanced ? '#FFF' : SOFT,
                border: `1px solid ${showAdvanced ? GOLD : LINE}`,
              }}>
              <div className="text-left">
                <p className="text-[10.5px] tracking-[0.18em] uppercase" style={{ color: GOLD, fontWeight: 600 }}>
                  Advanced Tailoring Options
                </p>
                <p className="text-[10px]" style={{ color: MUTED }}>
                  Most customers can skip this — your tailor verifies these.
                </p>
              </div>
              {showAdvanced
                ? <ChevronUp  className="w-4 h-4" style={{ color: MUTED }} strokeWidth={1.5} />
                : <ChevronDown className="w-4 h-4" style={{ color: MUTED }} strokeWidth={1.5} />}
            </button>
            {showAdvanced && (
              <div className="bg-white border border-t-0" style={{ borderColor: GOLD }}>
                {advancedKeys.map(k => (
                  <GarmentRow
                    key={k}
                    fieldKey={k}
                    derivedValue={derived[k]}
                    currentEdit={edits[k]}
                    onCommit={(n) => setEditValue(k, n)}
                    confidence={fieldConfidence({ fieldKey: k, baseConfidence: animConf || conf, hasPhoto: photoCount > 0 })}
                    easeCm={easeApplied[k]}
                    provenance={provenance[k]}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        <p className="text-[9.5px] mt-3 text-center flex items-center justify-center gap-1" style={{ color: MUTED }}>
          <ShieldCheck className="w-2.5 h-2.5" style={{ color: GREEN }} strokeWidth={1.6} />
          Tailor can fine-tune at first fitting — no measurement knowledge needed.
        </p>

        <div className="h-4" />
      </div>

      {/* Footer is a normal flex item (not fixed) so it can never cover the fit options. */}
      <div data-testid="review-footer" className="shrink-0 bg-white border-t" style={{ borderColor: LINE }}>
        <div className="px-4 py-3">
          <button data-testid="continue-checkout-btn" onClick={continueWithEdits}
            className="w-full py-3 text-[12px] tracking-[0.12em] uppercase text-white flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg, #E5B2B9, #D4AF37)', fontWeight: 500 }}>
            Continue to Checkout <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditableReview;
